/**
sc/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2019-2023 (original work) Open Assessment Technologies SA
 *
 */

import config from '@/config';
import { getPermissionsGuard } from './guards/permissionsGuard';
import { routing as loginRouting } from '@/modules/login/routing';
import { routing as groupsRouting } from '@/modules/groups/routing';
import { routing as batteriesRouting } from '@/modules/batteries/routing';
import { routing as studentSessionsRouting } from '@/modules/studentSessions/routing';
import { routing as personalSpaceRouting } from '@/modules/personalSpace/routing';
import { routing as usersRouting } from '@/modules/users/routing';
import { routing as sessionsRouting } from '@/modules/sessions/routing';
import { routing as organizationRouting } from '@/modules/organization/routing';
import { routing as reportsRouting } from '@/modules/reports/routing';
import { routing as importRouting } from '@/modules/import/routing';
import { routing as dashboardsRouting } from '@/modules/dashboards/routing';
import { routing as deliveriesRouting } from '@/modules/deliveries/routing';
import { routing as explorerRouting } from '@/modules/explorer/routing';
import { routing as supportRouting } from '@/modules/support/routing';
import { routing as assetsRouting } from '@/modules/assets/routing';
import { routing as campaignsRouting } from '@/modules/campaigns/routing';
import { routing as testsRouting } from '@/modules/tests/routing';
import emailVerificationRouting from '@/modules/emailVerifications/routing';
import emailSubscriptionsRouting from '@/modules/emailSubscriptions/routing';
import { routing as publicSessionsRouting } from '@/modules/publicSessions/routing';
import { routing as systemSettingsRouting } from '@/modules/systemSettings/routing';
import { routing as sessionTemplatesRouting } from '@/modules/sessionTemplates/routing';
import certificationSessionsRouting from '@/modules/certificationSessions/routing';
import { routing as aiLabsRouting } from '@/modules/aiLabs/routing';
import { routing as candidatesRouting } from '@/modules/candidates/routing';
import { routing as scoringProjectsRouting } from '@/modules/scoringProjects/routing';

/**
 * @callback Controller
 * @returns {Promise}
 */

/**
 * @typedef Route
 * @property {string} path Abstract path that match to url
 * @property {Controller} controller
 * @property {import('./guards/permissionsGuard').Guard} guard
 */

/**
 * @var Route[]
 */
const routeTable = [
    ...loginRouting,
    {
        path: [config.routes.home],
        guard: getPermissionsGuard([], false, true),
        controller: () => import('@/controller/home/home')
    },
    ...studentSessionsRouting,
    {
        path: [config.routes.contentBank],
        guard: getPermissionsGuard([{ resource: 'portal.content-bank', scopes: ['view'] }]),
        controller: () => import('@/controller/contentBank/contentBank')
    },
    ...groupsRouting,
    ...batteriesRouting,
    ...usersRouting,
    {
        path: [config.routes.diagnostic],
        guard: getPermissionsGuard([], true),
        controller: () => import('@/controller/diagnostic/diagnostic')
    },
    ...personalSpaceRouting,
    ...sessionsRouting,
    ...organizationRouting,
    ...reportsRouting,
    ...importRouting,
    ...dashboardsRouting,
    ...deliveriesRouting,
    ...explorerRouting,
    ...assetsRouting,
    ...campaignsRouting,
    ...testsRouting,
    ...emailVerificationRouting,
    ...emailSubscriptionsRouting,
    ...publicSessionsRouting,
    ...systemSettingsRouting,
    ...sessionTemplatesRouting,
    ...supportRouting,
    ...certificationSessionsRouting,
    ...aiLabsRouting,
    ...candidatesRouting,
    ...scoringProjectsRouting,
    // Default (error)
    {
        path: '(.*)',
        guard: getPermissionsGuard([], true),
        controller: () => import('@/controller/error/error')
    }
];

export default routeTable;
